
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAfter, parseISO, format, startOfDay, isEqual } from 'date-fns';
import { OverdueTasksDialog } from './OverdueTasksDialog';

interface OverdueTasksContextType {
  showOverdueDialog: boolean;
  setShowOverdueDialog: (show: boolean) => void;
  refreshOverdueTasks: () => Promise<void>;
  overdueCount: number;
}

// Create the context with a default value
export const OverdueTasksContext = createContext<OverdueTasksContextType>({
  showOverdueDialog: false,
  setShowOverdueDialog: () => {},
  refreshOverdueTasks: async () => {},
  overdueCount: 0
});

export const useOverdueTasks = () => {
  const context = useContext(OverdueTasksContext);
  if (!context) {
    throw new Error("useOverdueTasks must be used within an OverdueTasksProvider");
  }
  return context;
};

// Create the provider component that will be exported
export const OverdueTasksProvider = ({ children }: { children: React.ReactNode }) => {
  const [showOverdueDialog, setShowOverdueDialog] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const [initialLoginCheck, setInitialLoginCheck] = useState(false);
  const { session, user } = useAuth();
  const dialogShownRef = useRef(false);
  const refreshInProgressRef = useRef(false);

  // Function to refresh overdue tasks count
  const refreshOverdueTasks = async () => {
    if (!user || refreshInProgressRef.current) return;
    
    try {
      refreshInProgressRef.current = true;
      console.info("Fetching pending tasks for user:", user.id);
      const now = new Date();
      
      // Get user's organization name
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_name')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return;
      }
      
      if (!profile?.organization_name) {
        console.error("Could not find organization for user");
        return;
      }
      
      // Fetch kanban cards
      const { data: kanbanCards, error: kanbanError } = await supabase
        .from('kanban_cards')
        .select('id, due_date')
        .eq('organization_name', profile.organization_name)
        .neq('status', 'Terminado') // Exclude completed tasks
        .not('due_date', 'is', null);
      
      if (kanbanError) {
        console.error("Error fetching kanban cards:", kanbanError);
        return;
      }
      
      // Filter for overdue kanban cards - compare exact timestamps
      const overdueCards = kanbanCards?.filter(card => {
        if (!card.due_date) return false;
        const cardDueDate = new Date(card.due_date);
        return cardDueDate < now;
      }) || [];
      
      // Fetch overdue reminders
      const { data: reminders, error: reminderError } = await supabase
        .from('reminders')
        .select('id, due_date')
        .eq('user_id', user.id)
        .not('due_date', 'is', null);
      
      if (reminderError) {
        console.error("Error fetching reminders:", reminderError);
        return;
      }
      
      // Filter for overdue reminders
      const overdueReminders = reminders?.filter(reminder => {
        if (!reminder.due_date) return false;
        const reminderDueDate = new Date(reminder.due_date);
        return reminderDueDate < now;
      }) || [];
      
      // Fetch overdue progress reminders if user is admin
      let overdueProgressReminders: any[] = [];
      const isAdminUser = user?.email?.endsWith('@revenuehublatam.com') || user?.email?.endsWith('@digitalgrowth.cl');
      
      if (isAdminUser) {
        const { data: progressReminders, error: progressError } = await supabase
          .from('focusflow_progress_reminders')
          .select('id, due_date')
          .not('due_date', 'is', null);
        
        if (progressError) {
          console.error("Error fetching progress reminders:", progressError);
        } else {
          // Filter for overdue progress reminders
          overdueProgressReminders = progressReminders?.filter(reminder => {
            if (!reminder.due_date) return false;
            const reminderDueDate = new Date(reminder.due_date);
            return reminderDueDate < now;
          }) || [];
        }
      }
      
      // Set counts and show dialog if there are overdue items
      const totalOverdue = overdueCards.length + overdueReminders.length + overdueProgressReminders.length;
      console.info("Pending tasks count:", totalOverdue);
      
      if (totalOverdue > 0) {
        console.info("Found overdue cards:", overdueCards.length);
        console.info("Found overdue reminders:", overdueReminders.length);
        console.info("Found overdue progress reminders:", overdueProgressReminders.length);
      }
      
      setOverdueCount(totalOverdue);
      
      if (totalOverdue > 0 && !initialLoginCheck && !dialogShownRef.current && shouldShowDialogToday()) {
        // Add a small delay to ensure the dialog shows after the page loads
        // and track that we've shown the dialog to prevent multiple showings
        dialogShownRef.current = true;
        
        // Mark the dialog as shown today to prevent it from showing again today
        markDialogAsShownToday();
        
        setTimeout(() => {
          setShowOverdueDialog(true);
          setInitialLoginCheck(true);
        }, 500);
      }
      
    } catch (error) {
      console.error("Error checking overdue tasks:", error);
    } finally {
      refreshInProgressRef.current = false;
    }
  };

  // Determine if the dialog should be shown today based on localStorage
  const shouldShowDialogToday = () => {
    if (!user?.id) return false;
    
    // Get the last date the dialog was shown for this user
    const lastShownKey = `overdue_dialog_last_shown_${user.id}`;
    const lastShownDate = localStorage.getItem(lastShownKey);
    
    if (!lastShownDate) {
      return true; // Never shown before, so show it
    }
    
    // Compare the current date (without time) with the stored date
    const today = startOfDay(new Date());
    const lastShown = startOfDay(new Date(lastShownDate));
    
    // Only show if the dates are different (meaning it's a new day)
    return !isEqual(today, lastShown);
  };
  
  // Save the current date as the last shown date
  const markDialogAsShownToday = () => {
    if (!user?.id) return;
    
    const lastShownKey = `overdue_dialog_last_shown_${user.id}`;
    const today = new Date().toISOString();
    localStorage.setItem(lastShownKey, today);
  };

  // Show dialog only on initial login - ensuring it only happens once per session
  useEffect(() => {
    if (session && !initialLoginCheck && !dialogShownRef.current) {
      // Add a small delay to ensure auth data is fully loaded
      const timer = setTimeout(() => {
        // Only proceed with showing the dialog if it hasn't been shown today
        if (shouldShowDialogToday()) {
          refreshOverdueTasks();
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [session, initialLoginCheck]);

  // Reset dialog shown flag when dialog is closed
  useEffect(() => {
    if (!showOverdueDialog) {
      // Only reset the flag when we explicitly close the dialog
      // This allows us to track when the dialog has been manually closed
      if (initialLoginCheck) {
        dialogShownRef.current = true; // Keep it true if we've shown it once
      }
    }
  }, [showOverdueDialog, initialLoginCheck]);

  const contextValue = {
    showOverdueDialog,
    setShowOverdueDialog,
    refreshOverdueTasks,
    overdueCount
  };

  return (
    <OverdueTasksContext.Provider value={contextValue}>
      {children}
      <OverdueTasksDialog />
    </OverdueTasksContext.Provider>
  );
};

// The NotificationBanner component that renders the provider
const NotificationBanner = () => {
  // Fix: Pass children to OverdueTasksProvider even if it's an empty fragment
  return (
    <OverdueTasksProvider>
      <></>
    </OverdueTasksProvider>
  );
};

export default NotificationBanner;
